import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, FormControl, Grid, InputLabel, MenuItem,
  Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography, Alert, Chip
} from '@mui/material';
import { Calculate, Save } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getApartments, getApartmentUsers, createPerFlatExpense, getExpenseCategories } from '../services/api';

export default function WaterBill() {
  const [apartments, setApartments] = useState([]);
  const [selectedApartment, setSelectedApartment] = useState('');
  const [apartmentUsers, setApartmentUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [totalBillAmount, setTotalBillAmount] = useState('');
  const [readings, setReadings] = useState([]);
  const [calculated, setCalculated] = useState(false);
  const [costPerUnit, setCostPerUnit] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { isAdmin } = useAuth();

  useEffect(() => {
    getApartments().then(res => {
      setApartments(res.data);
      if (res.data.length > 0) setSelectedApartment(res.data[0].id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedApartment) return;
    getApartmentUsers(selectedApartment).then(res => {
      setApartmentUsers(res.data);
      // Initialize readings for all flats
      setReadings(res.data.map(u => ({
        apartmentUserId: u.id,
        flatNumber: u.flatNumber,
        userName: u.userName,
        previousReading: '',
        currentReading: '',
        unitsConsumed: 0,
        amount: 0
      })));
      setCalculated(false);
    }).catch(() => {});
    getExpenseCategories(selectedApartment).then(res => setCategories(res.data)).catch(() => {});
  }, [selectedApartment]);

  const handleReadingChange = (index, field, value) => {
    setReadings(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Auto-calculate units
      const current = parseFloat(updated[index].currentReading) || 0;
      const previous = parseFloat(updated[index].previousReading) || 0;
      updated[index].unitsConsumed = current > previous ? current - previous : 0;
      return updated;
    });
    setCalculated(false);
  };

  const handleCalculate = () => {
    setError('');
    const bill = parseFloat(totalBillAmount);
    if (!bill || bill <= 0) {
      setError('Please enter total water bill amount');
      return;
    }

    const totalUnits = readings.reduce((sum, r) => sum + r.unitsConsumed, 0);
    if (totalUnits <= 0) {
      setError('Total units consumed must be greater than 0. Please enter meter readings.');
      return;
    }

    const rate = bill / totalUnits;
    setCostPerUnit(rate);

    setReadings(prev => prev.map(r => ({
      ...r,
      amount: Math.round(r.unitsConsumed * rate * 100) / 100
    })));
    setCalculated(true);
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    // Find water bill category
    const waterCategory = categories.find(c => c.name.toLowerCase().includes('water'));
    if (!waterCategory) {
      setError('Water Bill category not found. Please create a "Water Bill" expense category first.');
      return;
    }

    const validItems = readings.filter(r => r.unitsConsumed > 0 && r.amount > 0);
    if (validItems.length === 0) {
      setError('No valid readings to save');
      return;
    }

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    try {
      await createPerFlatExpense({
        apartmentId: selectedApartment,
        categoryId: waterCategory.id,
        description: `Water Bill - ${monthNames[month - 1]} ${year}`,
        expenseDate: `${year}-${month.toString().padStart(2, '0')}-01`,
        lineItems: validItems.map(r => ({
          apartmentUserId: r.apartmentUserId,
          flatNumber: r.flatNumber,
          amount: r.amount,
          meterReading: parseFloat(r.currentReading) || null,
          previousReading: parseFloat(r.previousReading) || null,
          unitsConsumed: r.unitsConsumed,
          notes: `${r.unitsConsumed} units × ₹${costPerUnit.toFixed(2)}/unit`
        }))
      });
      setSuccess(`Water bill saved successfully! ₹${totalBillAmount} split across ${validItems.length} flats.`);
      setCalculated(false);
      setTotalBillAmount('');
      setReadings(prev => prev.map(r => ({ ...r, previousReading: '', currentReading: '', unitsConsumed: 0, amount: 0 })));
    } catch (err) {
      setError(err.response?.data || 'Failed to save water bill');
    }
  };

  const totalUnits = readings.reduce((sum, r) => sum + r.unitsConsumed, 0);
  const totalCalculatedAmount = readings.reduce((sum, r) => sum + r.amount, 0);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Water Bill Calculator</Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Enter meter readings for each flat. The total bill will be split based on actual water usage.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Apartment</InputLabel>
            <Select value={selectedApartment} label="Apartment" onChange={(e) => setSelectedApartment(e.target.value)}>
              {apartments.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Month</InputLabel>
            <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
              {Array.from({ length: 12 }, (_, i) => <MenuItem key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Year</InputLabel>
            <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
              {Array.from({ length: 5 }, (_, i) => <MenuItem key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField fullWidth size="small" label="Total Water Bill (₹)" type="number"
            value={totalBillAmount} onChange={(e) => { setTotalBillAmount(e.target.value); setCalculated(false); }}
            placeholder="e.g. 5000" />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button variant="contained" fullWidth startIcon={<Calculate />} onClick={handleCalculate}
            sx={{ height: '40px' }}>
            Calculate
          </Button>
        </Grid>
      </Grid>

      {/* Summary Cards */}
      {calculated && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: '#e3f2fd' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Total Bill</Typography>
                <Typography variant="h6" color="primary">₹{parseFloat(totalBillAmount).toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: '#e8f5e9' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Total Units</Typography>
                <Typography variant="h6" color="success.main">{totalUnits.toFixed(0)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: '#fff3e0' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Cost Per Unit</Typography>
                <Typography variant="h6" color="warning.main">₹{costPerUnit.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: '#fce4ec' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Calculated Total</Typography>
                <Typography variant="h6" color="error">₹{totalCalculatedAmount.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Meter Readings Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Flat</TableCell>
              <TableCell>Resident</TableCell>
              <TableCell align="center">Previous Reading</TableCell>
              <TableCell align="center">Current Reading</TableCell>
              <TableCell align="center">Units Consumed</TableCell>
              {calculated && <TableCell align="right">Amount (₹)</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {readings.map((r, idx) => (
              <TableRow key={r.apartmentUserId}>
                <TableCell><strong>{r.flatNumber}</strong></TableCell>
                <TableCell>{r.userName}</TableCell>
                <TableCell align="center">
                  <TextField size="small" type="number" variant="outlined"
                    value={r.previousReading}
                    onChange={(e) => handleReadingChange(idx, 'previousReading', e.target.value)}
                    sx={{ width: { xs: 70, sm: 100 } }}
                    inputProps={{ style: { textAlign: 'center' } }} />
                </TableCell>
                <TableCell align="center">
                  <TextField size="small" type="number" variant="outlined"
                    value={r.currentReading}
                    onChange={(e) => handleReadingChange(idx, 'currentReading', e.target.value)}
                    sx={{ width: { xs: 70, sm: 100 } }}
                    inputProps={{ style: { textAlign: 'center' } }} />
                </TableCell>
                <TableCell align="center">
                  <Chip label={r.unitsConsumed.toFixed(0)} size="small"
                    color={r.unitsConsumed > 0 ? 'primary' : 'default'} />
                </TableCell>
                {calculated && (
                  <TableCell align="right">
                    <Typography fontWeight="bold">₹{r.amount.toFixed(2)}</Typography>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {/* Totals Row */}
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell colSpan={4}><strong>Total</strong></TableCell>
              <TableCell align="center"><strong>{totalUnits.toFixed(0)}</strong></TableCell>
              {calculated && <TableCell align="right"><strong>₹{totalCalculatedAmount.toFixed(2)}</strong></TableCell>}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Save Button 
      {calculated && isAdmin() && (
        <Box sx={{ mt: 2, textAlign: 'right' }}>
          <Button variant="contained" color="success" startIcon={<Save />} onClick={handleSave} size="large">
            Save Water Bill as Expense
          </Button>
        </Box>
      )} */}
    </Box>
  );
}
